
import { defineComponent, PropType } from 'vue';
import { apiDelete, apiGet, apiPatch, apiPost } from "@/services/api/client";
import { Incident, IncidentEntry, IncidentStatus } from 'shared/Incident';
import { formatDateToUTC } from "@/utils/format";
import BaseModal from '@/components/ui/BaseModal.vue';

export default defineComponent({
  name: 'IncidentDetailView',
  components: { BaseModal },
  props: {
    incident: {
      type: Object as PropType<Incident>,
      required: true,
    },
  },
  data() {
    return {
      showEntryModal: false,
      newEntry: { author: '', message: '', createdAt: '' },
      statusUpdate: this.incident.status as IncidentStatus,
      editingEntryId: null as string | null,
      editingEntryMessage: '',
      isDownloadingPostmortem: false,
    };
  },
  computed: {
    sortedEntries(): IncidentEntry[] {
      // Newest at the top
      return [...this.incident.entries].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
  },
  methods: {
    formatDate(date: string | number) {
      return formatDateToUTC(date, (this as any).$i18n?.locale);
    },
    linkify(text: string) {
      if (!text) return '';
      const urlRegex = /(https?:\/\/[\w\-._~:/?#[\]@!$&'()*+,;=%]+)/g;
      return text.replace(urlRegex, url =>
        `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
      );
    },
    async downloadMarkdown() {
      try {
        const blob = await apiGet(`/incidents/${this.incident._id}/export-md`, {
          responseType: 'blob'
        }) as unknown as Blob;
        this.triggerBlobDownload(blob, `incident_${this.incident._id}.md`);
      } catch (error) {
        alert(this.$t('panel.incidents.errors.downloadMarkdown'));
      }
    },
    async downloadPostmortem() {
      this.isDownloadingPostmortem = true;
      try {
        const blob = await apiGet(`/incidents/${this.incident._id}/postmortem-md`, {
          responseType: 'blob'
        }) as unknown as Blob;
        this.triggerBlobDownload(blob, `incident_${this.incident._id}_postmortem.md`);
      } catch (error) {
        alert(this.$t('panel.incidents.errors.postmortem'));
      } finally {
        this.isDownloadingPostmortem = false;
      }
    },
    triggerBlobDownload(blob: Blob, filename: string) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    async addEntry() {
      if (!this.newEntry.author || !this.newEntry.message) return;
      const payload = { ...this.newEntry } as Partial<typeof this.newEntry>;
      if (!payload.createdAt) delete payload.createdAt;
      const entry = await apiPost(`/incidents/${this.incident._id}/entries`, payload) as IncidentEntry;
      this.incident.entries.push(entry);
      this.showEntryModal = false;
      this.newEntry = { author: '', message: '', createdAt: '' };
    },
    async updateStatus() {
      await apiPatch(`/incidents/${this.incident._id}/status`, { status: this.statusUpdate });
      this.incident.status = this.statusUpdate;
    },
    async startEditEntry(entry: IncidentEntry) {
      this.editingEntryId = entry.id;
      this.editingEntryMessage = entry.message;
    },
    async saveEditEntry(entry: IncidentEntry) {
      if (!this.editingEntryMessage.trim()) return;
      try {
        const updated = await apiPatch(`/incidents/${this.incident._id}/entries/${entry.id}`, { message: this.editingEntryMessage }) as IncidentEntry;
        entry.message = updated.message;
        this.editingEntryId = null;
        this.editingEntryMessage = '';
      } catch (error) {
        console.error('Failed to update entry:', error);
        alert(this.$t('panel.incidents.errors.updateEntry'));
      }
    },
    cancelEditEntry() {
      this.editingEntryId = null;
      this.editingEntryMessage = '';
    },
    async deleteEntry(entry: IncidentEntry) {
      if (!confirm(this.$t('panel.incidents.detail.confirmDelete'))) return;
      try {
        await apiDelete(`/incidents/${this.incident._id}/entries/${entry.id}`);
        const idx = this.incident.entries.findIndex(e => e.id === entry.id);
        if (idx !== -1) this.incident.entries.splice(idx, 1);
      } catch (error) {
        alert(this.$t('panel.incidents.errors.deleteEntry'));
      }
    },
  },
});
